import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Lock, Mail, School } from "lucide-react";
import { login } from "../../auth/authService";
import { supabase } from "../../auth/supabaseClient";
import { modeTheme } from "../../core/theme/modeTheme";
import { getModeFromPath } from "../../core/utils/getMode";
import {
  getDefaultPathForHostname,
  getForcedModeForHostname,
  resolveOriginalPath,
} from "../../core/utils/modeRouting";
import EmailNotConfirmedModal from "../components/EmailNotConfirmedModal";
import GlobalLoadingPage from "../components/GlobalLoadingPage";
import ActionToast from "../components/ActionToast";
import useMinimumLoading from "../hooks/useMinimumLoading";
import {
  BRAND_ASSET_KEYS,
  getBrandAssetSrc,
  getModeLogoSrc,
} from "../branding/brandAssets";

const CampusBackground = getBrandAssetSrc(BRAND_ASSET_KEYS.CAMPUS_BACKGROUND);
const ClassroomLoginBackground = getBrandAssetSrc(BRAND_ASSET_KEYS.CLASSROOM_LOGIN_BACKGROUND);
const EduIcon = getBrandAssetSrc(BRAND_ASSET_KEYS.EDU_ICON);
const EduLoginBackground = getBrandAssetSrc(BRAND_ASSET_KEYS.EDU_LOGIN_BACKGROUND);
const OIKOS_EDU_ORANGE = "#ff7a00";

function getCachedOrganizationAccount(type = "") {
  if (typeof window === "undefined" || !type) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(`oikos.organization.${type}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function getCampusLoginBackground(account = null, fallbackImage = CampusBackground) {
  const deviceBackground =
    account?.integrations?.eduStudentDevice?.background ||
    account?.deviceBackground ||
    {};
  const deviceLoginBackground =
    account?.integrations?.eduStudentDevice?.loginBackground ||
    account?.deviceLoginBackground ||
    {};
  const campusLoginBackground =
    account?.integrations?.campus?.loginBackground ||
    account?.campusLoginBackground ||
    {};
  const deviceLoginUsesOwnBackground = deviceLoginBackground.useDeviceBackground === false;
  const imageUrl =
    (deviceLoginUsesOwnBackground ? deviceLoginBackground.imageUrl : "") ||
    deviceBackground.imageUrl ||
    deviceLoginBackground.imageUrl ||
    campusLoginBackground.imageUrl ||
    "";
  const color =
    (deviceLoginUsesOwnBackground ? deviceLoginBackground.color : "") ||
    deviceBackground.color ||
    deviceLoginBackground.color ||
    campusLoginBackground.color ||
    "#fff7ed";
  return {
    imageUrl: imageUrl || fallbackImage,
    color,
  };
}

function getCampusPageBackgroundStyle(background = {}, options = {}) {
  const imageUrl = background.imageUrl || CampusBackground;
  const overlay = options.withOverlay === false
    ? ""
    : "linear-gradient(90deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.9) 34%, rgba(255, 255, 255, 0.38) 68%, rgba(255, 255, 255, 0.08) 100%), ";

  return {
    backgroundColor: background.color || "#fff7ed",
    backgroundImage: `${overlay}url(${imageUrl})`,
  };
}

export default function LoginPage({ forcedOriginalPath = "", forcedMode = "" }) {
  const location = useLocation();
  const { schoolCode: routeSchoolCode = "" } = useParams();
  const initialSchoolCode =
    routeSchoolCode || new URLSearchParams(location.search).get("schoolCode") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolCode, setSchoolCode] = useState(initialSchoolCode.toUpperCase());
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolLookupLoading, setSchoolLookupLoading] = useState(false);
  const [schoolLookupError, setSchoolLookupError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [toastError, setToastError] = useState("");

  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const isTeacherMode = forcedMode === "teacher";
  const isParentMode = forcedMode === "parent";
  const isFrontEduLogin = /^\/login(?:\/[^/]+)?\/?$/.test(location.pathname) && !forcedOriginalPath && !forcedMode;
  const isCampusTeacherPath = (value = "") => value.startsWith("/campus/teacher");
  const teacherLoginRoute =
    isTeacherMode ||
    forcedOriginalPath.startsWith("/teacher") ||
    isCampusTeacherPath(forcedOriginalPath) ||
    location.pathname.startsWith("/teacher");
  const parentLoginRoute =
    isParentMode || forcedOriginalPath.startsWith("/parent") || location.pathname.startsWith("/parent");
  const portalFallbackPath = teacherLoginRoute ? "/teacher" : parentLoginRoute ? "/parent" : "";
  const fallbackPath =
    forcedOriginalPath || portalFallbackPath || (isFrontEduLogin ? "/edu" : getDefaultPathForHostname(hostname));
  const resolvedOriginalPath = resolveOriginalPath(location.state?.from, hostname, fallbackPath);
  const originalPath = forcedOriginalPath || resolvedOriginalPath;

  const mode = isFrontEduLogin ? "edu" : isTeacherMode || isParentMode ? "campus" : getModeFromPath(originalPath, hostname);
  const isCampusMode = mode === "campus";
  const usesSchoolCodeFirst = isCampusMode || isFrontEduLogin;
  const isManagedDisplayMode = false;
  const isTeacherLogin = isTeacherMode || originalPath.startsWith("/teacher") || isCampusTeacherPath(originalPath);
  const isParentLogin = isParentMode || originalPath.startsWith("/parent");

  const logo = getModeLogoSrc(mode);
  const cachedCampusAccount = usesSchoolCodeFirst
    ? getCachedOrganizationAccount(isFrontEduLogin ? "edu" : "campus") || getCachedOrganizationAccount("campus")
    : null;
  const orgBrandColor = String(cachedCampusAccount?.brand_color || "").trim();
  const normalizedOrgColor = orgBrandColor
    ? orgBrandColor.startsWith("#")
      ? orgBrandColor
      : `#${orgBrandColor}`
    : "";
  const resolvedLogo = isFrontEduLogin
    ? getBrandAssetSrc(BRAND_ASSET_KEYS.EDU_LOGO)
    : isCampusMode
      ? getBrandAssetSrc(BRAND_ASSET_KEYS.CAMPUS_LOGO)
      : logo;
  const isCampusFindSchool = usesSchoolCodeFirst && !selectedSchool?.id;
  const campusBackground = getCampusLoginBackground(
    isCampusFindSchool ? null : selectedSchool || cachedCampusAccount,
    isFrontEduLogin ? EduLoginBackground : isCampusFindSchool ? ClassroomLoginBackground : CampusBackground
  );
  const activeEduLoginBackground = isFrontEduLogin
    ? campusBackground.imageUrl || EduLoginBackground
    : "";
  const primaryColor =
    isFrontEduLogin
      ? OIKOS_EDU_ORANGE
      : usesSchoolCodeFirst && normalizedOrgColor
      ? normalizedOrgColor
      : modeTheme[mode]?.primary || "#2f6ea3";
  const showLoading = useMinimumLoading(loginLoading || schoolLookupLoading);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const signedInUser = await login(email, password);

      let teacherPortalAccess = false;
      let parentPortalAccess = false;
      let campusDefaultAccess = false;

      if (signedInUser?.id) {
        const { data: campusAccessRows, error: campusAccessError } = await supabase
          .from("user_access")
          .select("mode, has_access")
          .eq("user_id", signedInUser.id)
          .eq("platform", "campus")
          .in("mode", ["default", "teacher_portal", "parent_portal"]);

        if (campusAccessError) {
          throw campusAccessError;
        }

        teacherPortalAccess = (campusAccessRows || []).some(
          (row) => row.mode === "teacher_portal" && row.has_access === true
        );
        parentPortalAccess = (campusAccessRows || []).some(
          (row) => row.mode === "parent_portal" && row.has_access === true
        );
        campusDefaultAccess = (campusAccessRows || []).some(
          (row) => row.mode === "default" && row.has_access === true
        );
      }

      const forcedMode = getForcedModeForHostname(hostname);
      const forcedDefaultPath = getDefaultPathForHostname(hostname);

      if (isTeacherLogin || originalPath.startsWith("/teacher") || isCampusTeacherPath(originalPath)) {
        navigate(isCampusTeacherPath(originalPath) ? "/campus/teacher" : "/teacher", { replace: true });
        return;
      }

      if (isParentLogin || originalPath.startsWith("/parent")) {
        navigate("/parent", { replace: true });
        return;
      }

      if (
        forcedDefaultPath === "/campus" &&
        teacherPortalAccess &&
        !campusDefaultAccess
      ) {
        navigate("/campus/teacher", { replace: true });
        return;
      }

      if (
        forcedDefaultPath === "/campus" &&
        parentPortalAccess &&
        !teacherPortalAccess &&
        !campusDefaultAccess
      ) {
        navigate("/parent", { replace: true });
        return;
      }

      if (isFrontEduLogin) {
        sessionStorage.removeItem("lastPath");
        navigate("/edu", { replace: true });
        return;
      }

      if (forcedMode) {
        navigate(forcedDefaultPath, { replace: true });
        return;
      }

      navigate(originalPath, { replace: true });

    } catch (err) {
      if (err.message?.toLowerCase().includes("email not confirmed")) {
        setShowConfirmModal(true);
        setLoginLoading(false);
        return;
      }

      setToastError(err.message || "Could not sign in.");
      setLoginLoading(false);
    }
  };

  const handleSchoolLookup = async (event, codeOverride = "") => {
    event?.preventDefault();
    const cleanCode = String(codeOverride || schoolCode || "").trim().toUpperCase();

    if (!cleanCode) {
      setSchoolLookupError("School code is required.");
      return;
    }

    setSchoolLookupLoading(true);
    setSchoolLookupError("");

    try {
      let account = null;
      const { data: schoolData, error: schoolRpcError } = await supabase.rpc("edu_teacher_portal_get_school", {
        p_school_code: cleanCode,
      });

      if (!schoolRpcError && schoolData?.account) {
        account = {
          id: schoolData.account.id,
          name: schoolData.account.name,
          type: schoolData.account.type || "edu",
          invite_code: "",
          edu_device_code: schoolData.account.deviceCode || cleanCode,
          logo_url: schoolData.account.logoUrl || "",
          brand_color: schoolData.account.brandColor || "",
          deviceBackground: schoolData.account.deviceBackground || {},
          deviceLoginBackground: schoolData.account.deviceLoginBackground || {},
          campusLoginBackground: schoolData.account.campusLoginBackground || {},
          integrations: {
            eduStudentDevice: {
              background: schoolData.account.deviceBackground || {},
              loginBackground: schoolData.account.deviceLoginBackground || {},
            },
            campus: {
              loginBackground: schoolData.account.campusLoginBackground || {},
            },
          },
        };
      }

      if (!account) {
        const { data: campusAccount, error: campusError } = await supabase
          .from("accounts")
          .select("id, name, type, owner_user_id, invite_code, edu_device_code, logo_url, brand_color, integrations")
          .eq("edu_device_code", cleanCode)
          .eq("type", "campus")
          .maybeSingle();

        if (campusError) {
          throw schoolRpcError || campusError;
        }

        account = campusAccount;
      }

      if (!account) {
        const { data: inviteAccount, error: inviteError } = await supabase
          .from("accounts")
          .select("id, name, type, owner_user_id, invite_code, edu_device_code, logo_url, brand_color, integrations")
          .eq("invite_code", cleanCode)
          .in("type", ["campus", "edu"])
          .limit(1)
          .maybeSingle();

        if (inviteError) {
          throw inviteError;
        }

        account = inviteAccount;
      }

      if (!account?.id) {
        throw new Error("School code was not found.");
      }

      const normalizedAccount = {
        ...account,
        edu_device_code: account.edu_device_code || cleanCode,
        logo_url: account.logo_url || "",
        logoUrl: account.logoUrl || account.logo_url || "",
        brand_color: account.brand_color || "",
        brandColor: account.brandColor || account.brand_color || "",
        deviceBackground: account.deviceBackground || account.device_background || {},
        deviceLoginBackground: account.deviceLoginBackground || account.device_login_background || {},
        campusLoginBackground: account.campusLoginBackground || account.campus_login_background || {},
        integrations: account.integrations || {},
      };

      setSelectedSchool(normalizedAccount);
      setSchoolCode(normalizedAccount.edu_device_code || cleanCode);

      if (typeof window !== "undefined") {
        const cacheKey = isFrontEduLogin ? "edu" : "campus";
        window.localStorage.setItem(`oikos.organization.${cacheKey}`, JSON.stringify(normalizedAccount));
        if (normalizedAccount.type === "campus") {
          window.localStorage.setItem("oikos.organization.campus", JSON.stringify(normalizedAccount));
        }
        window.dispatchEvent(new CustomEvent("oikos-theme-change", {
          detail: {
            mode: isFrontEduLogin ? "edu" : "campus",
            accountId: normalizedAccount.id,
          },
        }));
      }
    } catch (lookupError) {
      setSelectedSchool(null);
      setSchoolLookupError(lookupError?.message || "School code was not found.");
    } finally {
      setSchoolLookupLoading(false);
    }
  };

  useEffect(() => {
    if (!usesSchoolCodeFirst || selectedSchool?.id || !initialSchoolCode) {
      return;
    }

    handleSchoolLookup(null, initialSchoolCode);
  }, []);

  return (
    <div
      style={
        usesSchoolCodeFirst
          ? {
              ...pageStyle,
              ...campusPageStyle,
              ...(isFrontEduLogin ? eduLoginPageStyle : {}),
              ...getCampusPageBackgroundStyle(campusBackground, { withOverlay: isFrontEduLogin ? false : !isCampusFindSchool }),
            }
          : pageStyle
      }
    >
      {showLoading ? (
        <GlobalLoadingPage
          title={schoolLookupLoading ? "Finding School" : "Signing In"}
          detail={schoolLookupLoading ? "Looking up your Oikos EDU organization." : "Opening your organization workspace."}
          modeOverride={mode}
          primaryColorOverride={primaryColor}
          iconOverride={isFrontEduLogin ? EduIcon : ""}
          iconFallbackOverride={isFrontEduLogin ? EduIcon : ""}
          cleanIconLoader={isFrontEduLogin}
          backgroundImageOverride={isFrontEduLogin ? activeEduLoginBackground : ""}
          labelOverride={
            isTeacherLogin
              ? "Teacher Portal"
              : isParentLogin
                ? "Parent Portal"
                : mode === "pages"
                  ? "Pages"
                  : mode === "tables"
                    ? "Tables"
                    : undefined
          }
        />
      ) : null}
      {isFrontEduLogin ? (
        <img
          aria-hidden="true"
          alt=""
          src={activeEduLoginBackground}
          style={eduLoginBackgroundImageStyle}
        />
      ) : null}
      <ActionToast message={toastError} tone="error" onDismiss={() => setToastError("")} />
      <ActionToast message={schoolLookupError} tone="error" onDismiss={() => setSchoolLookupError("")} />
      {isManagedDisplayMode ? (
        <div style={displayLogoWrapper}>
          <img
            src={resolvedLogo}
            alt="Oikos EDU"
            style={displayLogoStyle}
          />
        </div>
      ) : null}
      <div
        style={
          usesSchoolCodeFirst
            ? { ...cardStyle, ...campusCardStyle, ...(isFrontEduLogin ? eduLoginCardStyle : {}) }
            : cardStyle
        }
      >

        {!isManagedDisplayMode ? (
          <div style={logoWrapper}>
            <img
              src={resolvedLogo}
              alt={usesSchoolCodeFirst ? "Oikos EDU" : "Oikos"}
              style={
                isFrontEduLogin
                  ? { ...logoStyle, ...eduLoginLogoStyle }
                  : usesSchoolCodeFirst
                    ? { ...logoStyle, ...campusLogoStyle }
                    : logoStyle
              }
            />
          </div>
        ) : null}

        {!isFrontEduLogin ? (
          <div style={headingBlockStyle}>
            <div style={eyebrowStyle}>
              {isTeacherLogin
                ? "Teacher Portal"
                : isParentLogin
                  ? "Parent Portal"
                  : usesSchoolCodeFirst
                    ? "Oikos EDU"
                    : "Oikos Sign In"}
            </div>
            <div style={headingStyle}>
              {isTeacherLogin
                ? "Sign in to your teacher portal"
                : isParentLogin
                  ? "Sign in to your family portal"
                  : usesSchoolCodeFirst
                    ? selectedSchool?.name || "Find your school"
                    : "Sign in to Oikos"}
            </div>
            <div style={subheadingStyle}>
              {isTeacherLogin
                ? "Use your school-linked Oikos account to enter the teacher portal."
                : isParentLogin
                  ? "Use your family-linked Oikos account to see grades, assignments, and report cards."
                  : usesSchoolCodeFirst
                    ? selectedSchool?.id
                      ? "Sign in to your organization account."
                      : "Enter your school code to continue to Oikos EDU."
                    : "Use your Oikos account to continue."}
            </div>
          </div>
        ) : null}

        {usesSchoolCodeFirst && !selectedSchool?.id ? (
          <form style={formStyle} onSubmit={handleSchoolLookup}>
            {isFrontEduLogin ? (
              <label style={eduInputWrapStyle}>
                <School size={18} style={eduInputIconStyle} />
                <input
                  type="text"
                  value={schoolCode}
                  onChange={(event) => setSchoolCode(event.target.value.toUpperCase())}
                  autoComplete="organization"
                  autoFocus
                  placeholder="School code"
                  style={eduPillInputStyle}
                />
              </label>
            ) : (
              <>
                <label style={labelStyle}>School Code</label>
                <input
                  type="text"
                  value={schoolCode}
                  onChange={(event) => setSchoolCode(event.target.value.toUpperCase())}
                  autoComplete="organization"
                  autoFocus
                  style={inputStyle}
                />
              </>
            )}

            <button
              type="submit"
              disabled={schoolLookupLoading}
              style={{
                ...buttonStyle,
                ...(isFrontEduLogin ? eduButtonStyle : {}),
                background: primaryColor,
                opacity: schoolLookupLoading ? 0.72 : 1,
              }}
            >
              {schoolLookupLoading ? "Finding School..." : "Continue"}
            </button>
          </form>
        ) : (
        <form style={formStyle} onSubmit={handleLogin}>
          {usesSchoolCodeFirst && selectedSchool?.id ? (
            <button
              type="button"
              onClick={() => {
                setSelectedSchool(null);
                setPassword("");
                setSchoolLookupError("");
              }}
              style={isFrontEduLogin ? eduOrganizationButtonStyle : changeSchoolStyle}
            >
              {isFrontEduLogin ? (
                <>
                  <span style={eduOrganizationLogoFrameStyle}>
                    <img src={selectedSchool.logo_url || selectedSchool.logoUrl || EduIcon || resolvedLogo} alt="" style={eduOrganizationLogoStyle} />
                  </span>
                  <span style={eduOrganizationTextStyle}>
                    <span style={eduOrganizationLabelStyle}>Signing in to</span>
                    <span style={eduOrganizationNameStyle}>{selectedSchool.name || "Oikos EDU"}</span>
                    <span style={eduOrganizationCodeStyle}>{schoolCode}</span>
                  </span>
                </>
              ) : "Change School"}
            </button>
          ) : null}

          {isFrontEduLogin ? (
            <>
              <label style={eduInputWrapStyle}>
                <Mail size={18} style={eduInputIconStyle} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="Email"
                  style={eduPillInputStyle}
                />
              </label>

              <label style={eduInputWrapStyle}>
                <Lock size={18} style={eduInputIconStyle} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Password"
                  style={eduPillInputStyle}
                />
              </label>
            </>
          ) : (
            <>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
            </>
          )}

          <button
            type="submit"
            style={{ ...buttonStyle, ...(isFrontEduLogin ? eduButtonStyle : {}), background: primaryColor }}
          >
            Sign In
          </button>

          <div style={linksStyle}>
            <span onClick={() => navigate("/signup", { state: { from: originalPath } })} style={{ ...linkStyle, color: primaryColor }}>
              Create Account
            </span>

            <span onClick={() => navigate("/join", { state: { from: originalPath } })} style={{ ...linkStyle, color: primaryColor }}>
              Join with Code
            </span>

            <span onClick={() => navigate("/forgot-password")} style={{ ...linkStyle, color: primaryColor }}>
              Forgot Password?
            </span>
          </div>
        </form>
        )}

      </div>

      {showConfirmModal && (
        <EmailNotConfirmedModal
          email={email}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
}

// STYLES
const pageStyle = {
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  justifyContent: "center",
  alignItems: "center",
  background: "#f7f8fa",
};

const campusPageStyle = {
  minHeight: "100vh",
  padding: "24px",
  boxSizing: "border-box",
  backgroundColor: "#fff7ed",
  backgroundPosition: "center",
  backgroundSize: "cover",
};

const eduLoginPageStyle = {
  backgroundAttachment: "fixed",
  backgroundColor: "#fff7ed",
  display: "grid",
  height: "auto",
  inset: 0,
  justifyContent: "center",
  minHeight: "100dvh",
  overflow: "auto",
  padding: "20px",
  placeItems: "center",
  position: "fixed",
  width: "100vw",
};

const eduLoginBackgroundImageStyle = {
  height: "100%",
  inset: 0,
  objectFit: "cover",
  pointerEvents: "none",
  position: "fixed",
  width: "100%",
  zIndex: 0,
};

const cardStyle = {
  width: "100%",
  maxWidth: "420px",
  background: "#fff",
  borderRadius: "16px",
  padding: "20px",
};

const campusCardStyle = {
  maxWidth: "440px",
  padding: "28px",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 24px 70px rgba(15, 23, 42, 0.14)",
};

const eduLoginCardStyle = {
  background: "transparent",
  border: 0,
  borderRadius: 0,
  boxShadow: "none",
  display: "grid",
  gap: "12px",
  maxWidth: "360px",
  padding: "20px 10px",
  position: "relative",
  width: "100%",
  zIndex: 1,
};

const logoWrapper = {
  display: "flex",
  justifyContent: "center",
  marginBottom: "14px",
};

const displayLogoWrapper = {
  display: "flex",
  justifyContent: "center",
  maxWidth: "min(280px, calc(100vw - 48px))",
  width: "100%",
};

const displayLogoStyle = {
  height: "auto",
  maxHeight: "52px",
  objectFit: "contain",
  width: "118px",
};

const headingBlockStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "18px",
  textAlign: "center",
};

const eyebrowStyle = {
  color: "#0f766e",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const headingStyle = {
  color: "#0f172a",
  fontSize: "24px",
  fontWeight: 900,
};

const subheadingStyle = {
  color: "#64748b",
  fontSize: "14px",
  lineHeight: 1.6,
};

const logoStyle = {
  width: "180px",
};

const campusLogoStyle = {
  width: "360px",
  maxWidth: "96%",
};

const eduLoginLogoStyle = {
  height: "auto",
  justifySelf: "center",
  marginBottom: "8px",
  maxWidth: "100%",
  objectFit: "contain",
  width: "100%",
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
};

const labelStyle = {
  marginBottom: "5px",
};

const inputStyle = {
  marginBottom: "15px",
  padding: "10px",
  boxSizing: "border-box",
};

const eduInputWrapStyle = {
  alignItems: "center",
  background: "rgba(255, 255, 255, 0.86)",
  border: "1px solid rgba(255, 255, 255, 0.92)",
  borderRadius: 999,
  boxSizing: "border-box",
  boxShadow: "0 10px 24px rgba(255, 122, 0, 0.13)",
  display: "flex",
  gap: "10px",
  marginBottom: "12px",
  minHeight: "48px",
  padding: "0 16px",
  width: "100%",
};

const eduInputIconStyle = {
  color: OIKOS_EDU_ORANGE,
  flex: "0 0 auto",
};

const eduPillInputStyle = {
  background: "transparent",
  border: 0,
  boxSizing: "border-box",
  color: "#18202a",
  flex: 1,
  font: "500 15px inherit",
  minWidth: 0,
  outline: "none",
  padding: "13px 0",
  width: "100%",
};

const buttonStyle = {
  padding: "12px",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

const eduButtonStyle = {
  borderRadius: 999,
  boxShadow: "0 14px 28px rgba(255, 122, 0, 0.26)",
  fontWeight: 800,
  minHeight: "48px",
};

const eduOrganizationButtonStyle = {
  alignItems: "center",
  background: "rgba(255, 255, 255, 0.72)",
  backdropFilter: "blur(14px) saturate(1.08)",
  WebkitBackdropFilter: "blur(14px) saturate(1.08)",
  border: "1px solid rgba(255, 255, 255, 0.84)",
  borderRadius: 999,
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
  boxSizing: "border-box",
  color: "#2b1a10",
  cursor: "pointer",
  display: "flex",
  font: "inherit",
  gap: "10px",
  marginBottom: "12px",
  minHeight: "54px",
  padding: "7px 12px 7px 8px",
  textAlign: "left",
  width: "100%",
};

const eduOrganizationLogoFrameStyle = {
  alignItems: "center",
  background: "rgba(255, 255, 255, 0.7)",
  border: "1px solid rgba(255, 255, 255, 0.8)",
  borderRadius: 999,
  boxShadow: "0 8px 18px rgba(255, 122, 0, 0.16)",
  display: "flex",
  flex: "0 0 40px",
  height: "40px",
  justifyContent: "center",
  overflow: "hidden",
  padding: "6px",
  width: "40px",
};

const eduOrganizationLogoStyle = {
  display: "block",
  height: "100%",
  objectFit: "contain",
  width: "100%",
};

const eduOrganizationTextStyle = {
  display: "grid",
  flex: "1 1 auto",
  gap: "1px",
  minWidth: 0,
};

const eduOrganizationLabelStyle = {
  color: "#8a4b16",
  display: "block",
  fontSize: "11px",
  fontWeight: 800,
  lineHeight: 1.1,
  textTransform: "uppercase",
};

const eduOrganizationNameStyle = {
  color: "#2b1a10",
  display: "block",
  fontSize: "14px",
  fontWeight: 800,
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const eduOrganizationCodeStyle = {
  color: "#8a4b16",
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.25,
  overflowWrap: "anywhere",
};

const linksStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "10px",
  gap: "10px",
  flexWrap: "wrap",
};

const linkStyle = {
  cursor: "pointer",
  fontSize: "13px",
};

const errorStyle = {
  color: "#b42318",
  fontSize: "13px",
  fontWeight: 700,
  marginTop: "10px",
  textAlign: "center",
};

const changeSchoolStyle = {
  alignSelf: "center",
  background: "transparent",
  border: "none",
  color: "#64748b",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 800,
  marginBottom: "12px",
};
